// Shared invoice PDF generation pipeline.
//
// Renders the DOM pages of a mounted <Invoice /> (the host element) to a PDF
// blob via html2canvas + jsPDF. Used by:
//   - InvoiceExport.jsx  (download single / ZIP export)
//   - InvoiceProfile.jsx (Draft Email attachment)
//
// The host element must contain the rendered invoice pages as direct
// children (one child per page), matching how <Invoice /> renders.

const LETTER_WIDTH_MM = 215.9;
const LETTER_HEIGHT_MM = 279.4;
const DEFAULT_SCALE = 8; // higher scale for sharper PDFs

/**
 * Generate a PDF blob from a rendered invoice host element.
 * @param {HTMLElement} host - element containing the rendered invoice pages
 * @param {Object} [options]
 * @param {number} [options.scale=8] - html2canvas capture scale
 * @param {(status: string, progress: number) => void} [options.onProgress] - optional progress callback
 * @returns {Promise<Blob>} the PDF blob
 */
export const generateInvoicePdfBlob = async (host, options = {}) => {
  const { scale = DEFAULT_SCALE, onProgress } = options;
  if (!host) throw new Error("Render container missing");

  const pageElements = Array.from(host.children);
  if (!pageElements.length) throw new Error("No pages to export");

  // Remove border radius for PDF capture (restored in finally).
  const invoiceItems = host.querySelectorAll(".InvoiceItem");
  const originalStyles = [];
  invoiceItems.forEach((item) => {
    originalStyles.push(item.style.borderRadius);
    item.style.borderRadius = "0";
  });

  try {
    const { default: html2canvas } = await import("html2canvas");
    const jsPDF = (await import("jspdf")).default;

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: [LETTER_WIDTH_MM, LETTER_HEIGHT_MM],
    });

    for (let i = 0; i < pageElements.length; i++) {
      const pageElement = pageElements[i];
      onProgress?.(
        `Rendering page ${i + 1} of ${pageElements.length}…`,
        Math.round((i / pageElements.length) * 100),
      );

      const canvas = await html2canvas(pageElement, {
        scale,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
        imageSmoothingEnabled: true,
      });

      const imgData = canvas.toDataURL("image/png");

      if (i > 0) {
        pdf.addPage([LETTER_WIDTH_MM, LETTER_HEIGHT_MM], "portrait");
      }

      pdf.addImage(
        imgData,
        "JPEG",
        0,
        0,
        LETTER_WIDTH_MM,
        LETTER_HEIGHT_MM,
        undefined,
        "FAST",
      );

      // Overlay link annotations for anchors (mailto, http/https, tel) on this page
      const anchors = pageElement.querySelectorAll(
        'a[href^="mailto:"], a[href^="http://"], a[href^="https://"], a[href^="tel:"]',
      );
      if (anchors.length) {
        const pageRect = pageElement.getBoundingClientRect();
        const mmPerPxX = LETTER_WIDTH_MM / pageRect.width;
        const mmPerPxY = LETTER_HEIGHT_MM / pageRect.height;

        anchors.forEach((anchor) => {
          const rect = anchor.getBoundingClientRect();
          const href = anchor.getAttribute("href");
          if (!href) return;

          const xMm = (rect.left - pageRect.left) * mmPerPxX;
          const yMm = (rect.top - pageRect.top) * mmPerPxY;
          const wMm = rect.width * mmPerPxX;
          const hMm = rect.height * mmPerPxY;

          if (wMm > 0 && hMm > 0) {
            pdf.link(xMm, yMm, wMm, hMm, { url: href });
          }
        });
      }
    }

    return pdf.output("blob");
  } finally {
    invoiceItems.forEach((item, index) => {
      item.style.borderRadius = originalStyles[index];
    });
  }
};

/** Trigger a browser download for a blob. */
export const downloadBlob = (blob, fileName) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = fileName;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  // Cleanup the URL object to free memory
  setTimeout(() => URL.revokeObjectURL(url), 100);
};